import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  Paper,
  Typography,
  useMediaQuery,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Auth } from 'aws-amplify';
import BoardCard from '../../components/BoardCard';
import { useAccount } from '../../contexts/Account';
import eventBus, { eventBusValues } from './../../eventBus';
import calendar from '../../icons/calendar.svg';
import DualAccountModal from './DualAccountModal';
import { AccountType } from '../../services/utils/types';
import IncludedChip from '../../components/Advisor/IncludedChip';

const MyBoards = () => {
  const { accountDetails, isDualUser, authenticate } = useAccount();
  const [memberBoards, setMemberBoards] = useState(
    accountDetails?.boards?.memberBoards || []
  );
  const [hideScroll, setHideScroll] = useState(false);
  const [width, setWidth] = useState(0);
  const [openDualAccountModal, setOpenDualAccountModal] = useState(false);
  const [switchAccountLoading, setSwitchAccountLoading] = useState(false);

  const scrollContainerRef = useRef(null);
  const containerRef = useRef(null);
  const isXs = useMediaQuery((theme) => theme.breakpoints.down('xs'));

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const newWidth = containerRef.current.offsetWidth;
        const viewportWidth = window.innerWidth - 80;
        setWidth(Math.min(newWidth, viewportWidth));
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const boards = accountDetails?.boards?.memberBoards || [];
    setMemberBoards(boards);
  }, [accountDetails]);

  useEffect(() => {
    const handleBoardJoined = (board) => {
      setMemberBoards((prev) => {
        const exists = prev.some((item) => item.id === board.id);
        if (exists) return prev;
        return [...prev, board];
      });
    };

    eventBus.on(eventBusValues.advisorAnsweredInvitationV2, handleBoardJoined);
    return () => {
      eventBus.remove(
        eventBusValues.advisorAnsweredInvitationV2,
        handleBoardJoined
      );
    };
  }, []);

  useEffect(() => {
    if (!scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    const { scrollWidth } = container;
    if (scrollWidth <= width) {
      setHideScroll(true);
    } else {
      setHideScroll(false);
    }
  }, [width, memberBoards, isXs]);

  const sortedBoards = useMemo(() => {
    return [...memberBoards].sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt) : 0;
      const dateB = b.createdAt ? new Date(b.createdAt) : 0;
      return dateB - dateA;
    });
  }, [memberBoards]);

  const scrollRight = () => {
    if (!scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    const { scrollLeft, scrollWidth, clientWidth } = container;
    const jump = 300;

    if (scrollLeft + clientWidth + 50 >= scrollWidth) {
      container.scrollTo({
        left: 0,
        behavior: 'smooth',
      });
    } else {
      container.scrollBy({
        left: jump,
        behavior: 'smooth',
      });
    }
  };

  const scrollLeft = () => {
    if (!scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    const jump = 300;
    if (container.scrollLeft === 0) {
      container.scrollTo({ left: container.scrollWidth, behavior: 'smooth' });
    } else {
      container.scrollBy({ left: -jump, behavior: 'smooth' });
    }
  };

  const cardWidth = { xxs: '280px', xs: '350px' };

  if (!accountDetails) return null;

  return (
    <Paper
      elevation={3}
      sx={{
        padding: { xxs: '10px', xs: '20px' },
        borderRadius: { xxs: 0, xs: '16px' },
        backgroundColor: '#fff',
        marginTop: '20px',
        boxShadow: 'none',
        position: 'relative',
        maxWidth: { xxs: 'calc(100% + 26px)', xs: '100%' },
        mx: { xxs: '-13px', xs: 0 },
      }}
      id="my-boards-tile"
      ref={containerRef}
    >
      <DualAccountModal
        open={openDualAccountModal}
        setOpen={setOpenDualAccountModal}
      />
      <Typography
        sx={{
          fontSize: '20px',
          font: 'Poppins',
          fontWeight: 600,
          lineHeight: '26px',
          marginBottom: '0px',
          color: '#001030',
        }}
      >
        My Boards
      </Typography>
      <Typography
        sx={{
          fontWeight: '400',
          fontSize: { xxs: '12px', xs: '14px' },
          color: '#4D586E',
          fontStyle: 'italic',
          marginTop: '5px',
        }}
      >
        Joining a variety of advisory boards allows you to share your ideas
        across diverse companies and expand your exposure for professional
        opportunities and valuable benefits. AdvisoryCloud makes sharing your
        ideas across your portfolio of boards easy, exciting and rewarding.
      </Typography>

      {!hideScroll && (
        <IconButton
          onClick={scrollLeft}
          sx={{
            display: { xxs: 'none', xs: 'flex' },
            position: 'absolute',
            borderRadius: '8px',
            left: 10,
            top: '50%',
            height: 32,
            width: 32,
            transform: 'translateY(-50%)',
            zIndex: 10,
            color: '#000',
            backgroundColor: '#fff',
            boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
      )}

      <Box
        ref={scrollContainerRef}
        sx={{
          display: 'flex',
          overflowX: 'auto',
          overflowY: 'visible',
          whiteSpace: 'nowrap',
          scrollBehavior: 'smooth',
          overscrollBehaviorX: 'contain',
          marginRight: { xxs: 0, xs: hideScroll ? 0 : '40px' },
          paddingTop: '10px',
          maxWidth: {
            xxs: '100%',
            xs: Math.max(width - 40, 0),
            md: width + 200,
            lg: width + 500,
          },
        }}
      >
        <Box
          key="dual-account"
          sx={{
            marginBottom: '20px',
            paddingTop: '10px',
            marginRight: '16px',
            width: cardWidth,
          }}
        >
          <BoardCard
            board={{
              ...(accountDetails.companyDual || {}),
              companyName:
                isDualUser && accountDetails.companyDual?.companyName
                  ? accountDetails.companyDual.companyName
                  : `${accountDetails.givenName}'s Advisory Board`,
              headline:
                isDualUser && accountDetails.companyDual?.headline
                  ? accountDetails.companyDual.headline
                  : 'Included with your membership is the ability to start your own advisory board for a company, team or personal side project.',
            }}
            buttonText={
              isDualUser
                ? switchAccountLoading
                  ? 'Switching...'
                  : 'Switch to Company'
                : 'Get Started'
            }
            Chip={!isDualUser ? IncludedChip : null}
            hideMemberSince
            width={cardWidth}
            height="340px"
            staticTile
            customImage={
              accountDetails.companyDual?.image?.id ? null : calendar
            }
            headlineMarginBottom="40px"
            cardId={isDualUser ? 'dualTileFilled' : 'dualTileDefault'}
            buttonId={
              isDualUser ? 'dualSwitchUserTypeButton' : 'dualGetStartedButton'
            }
            onClick={async () => {
              if (isDualUser) {
                setSwitchAccountLoading(true);
                const user = await Auth.currentAuthenticatedUser();
                await Auth.updateUserAttributes(user, {
                  profile: AccountType.COMPANY,
                });
                await authenticate();
              } else {
                setOpenDualAccountModal(true);
              }
            }}
          />
        </Box>

        {sortedBoards.length > 0 ? (
          sortedBoards.map((board, index) => (
            <Box
              key={`member-board-${board.id || index}`}
              sx={{
                marginBottom: '20px',
                paddingTop: '10px',
                marginRight: '16px',
                width: cardWidth,
              }}
            >
              <BoardCard
                board={board}
                displayPillsFlag
                width={cardWidth}
                height="340px"
                buttonText="Enter Boardroom"
              />
            </Box>
          ))
        ) : (
          <Box
            key="join"
            sx={{
              marginBottom: '20px',
              paddingTop: '10px',
              marginRight: '16px',
              width: cardWidth,
            }}
          >
            <BoardCard
              board={{
                companyName: 'Join Advisory Boards!',
                headline:
                  'Our platform makes it easy and rewarding to be on boards built by 100s of organizations on AdvisoryCloud.',
              }}
              headlineMarginBottom="40px"
              width={cardWidth}
              height="340px"
            />
          </Box>
        )}
      </Box>

      {!hideScroll && (
        <IconButton
          onClick={scrollRight}
          sx={{
            display: { xxs: 'none', xs: 'flex' },
            position: 'absolute',
            borderRadius: '8px',
            right: 10,
            top: '50%',
            height: 32,
            width: 32,
            transform: 'translateY(-50%)',
            zIndex: 10,
            color: '#000',
            backgroundColor: '#fff',
            boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      )}
    </Paper>
  );
};

export default MyBoards;
